import * as React from "react";
import { MouseEventHandler, useCallback, useEffect, useMemo } from "react";
import { flatten } from "flat";
import { filter, isEmpty, isNil, pipe, toPairs } from "ramda";
import { styled } from "styled-components";

import { typeScale } from "lib/themeUtils";

export const Panel = styled.div`
  margin-bottom: 3rem;
  background: #f7ebeb;
  border-radius: 6px;
  overflow: hidden;
`;

export const PanelBody = styled.div`
  padding: 1rem;
`;

export const PanelHeader = styled.header`
  padding: 0.75rem 1rem;
  background: ${(props) => props.theme.colours.error};
  color: white;
`;

export const PanelTitle = styled.h2`
  margin: 0;
  ${typeScale.md};
`;

export const ErrorList = styled.ol`
  color: ${(props) => props.theme.colours.error};
`;

export const ErrorListItem = styled.li`
  color: ${(props) => props.theme.colours.error};
`;

export const ErrorListItemLink = styled.a`
  color: ${(props) => props.theme.colours.error};

  &:visited {
    color: ${(props) => props.theme.colours.error};
  }
`;
const ErrorItem: React.FC<{ id: string; error: string }> = ({ id, error }) => {
  const handleClick = useCallback<MouseEventHandler<HTMLAnchorElement>>(
    (e) => {
      const inputEl = document.getElementById(id) as HTMLInputElement | undefined;
      const labels = inputEl?.labels ?? [];
      const fieldLabel = labels[0];
      if (!isNil(fieldLabel)) {
        e.preventDefault();
        e.stopPropagation();
        history.replaceState("", document.title, window.location.pathname + window.location.search + `#${id}`);
        inputEl?.focus();
        fieldLabel.scrollIntoView();
      }
    },
    [id]
  );
  return (
    <ErrorListItem key={id}>
      <ErrorListItemLink onClick={handleClick} href={`#${id}`}>
        {error}
      </ErrorListItemLink>
    </ErrorListItem>
  );
};
const renderError = ([id, error]: [string, string]) => {
  return <ErrorItem id={id} error={error} key={id} />;
};

const filterEmpty = filter((el: [string, unknown]) => !isEmpty(el[1]));
const filterErrors = pipe(flatten, toPairs, filterEmpty);

export const ErrorPanel: React.FC<{ errors: ValidationErrors }> = ({ errors }) => {
  const filteredErrors = filterErrors(errors) as [string, string][];
  const areErrors = !isEmpty(filteredErrors);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const errorLines = useMemo(() => filteredErrors.map(renderError), [filteredErrors]);

  return areErrors ? (
    <Panel data-testid="error-panel">
      <PanelHeader>
        <PanelTitle>Fix the following errors</PanelTitle>
      </PanelHeader>
      <PanelBody>
        <ErrorList>{errorLines}</ErrorList>
      </PanelBody>
    </Panel>
  ) : (
    <></>
  );
};
